<template>
  <div data-cy="page-souscription-vigeo">
    <SlHeader
      :current-step="3"
      :links="headerLinks"
    />

    <sl-separator class="mb-4" />

    <personal-information-form
      v-model:num-secu="personalInformation.numSecu"
      v-model:organisme="personalInformation.organisme"
      v-model:num-secu-conjoint="personalInformation.numSecuConjoint"
      v-model:organisme-conjoint="personalInformation.organismeConjoint"
      :display-conjoint="displayConjoint"
      :server-errors="serverErrors"
    />

    <contribution-information-form
      v-model:fractionnement="contributionInformation.fractionnement"
      v-model:jour-prelevement="contributionInformation.jourPrelevement"
      v-model:date-effet="contributionInformation.dateEffet"
      :fractionnements="fractionnements"
      :jours-prelevement="joursPrelevement"
      :server-errors="serverErrors"
    />

    <bank-information-form
      v-model:nom-titulaire="bankInformation.nomTitulaire"
      v-model:iban="bankInformation.iban"
      v-model:nom-banque="bankInformation.nomBanque"
      v-model:adresse-banque="bankInformation.adresseBanque"
      v-model:code-postal-banque="bankInformation.codePostalBanque"
      v-model:ville-banque="bankInformation.villeBanque"
      :server-errors="serverErrors"
    />

    <insurer-information-form
      v-if="displayAssureur"
      v-model:nom-assureur="insurerInformation.nomAssureur"
      v-model:adresse-assureur="insurerInformation.adresseAssureur"
      v-model:code-postal-assureur="insurerInformation.codePostalAssureur"
      v-model:ville-assureur="insurerInformation.villeAssureur"
      v-model:pays-assureur="insurerInformation.paysAssureur"
      v-model:numero-contrat-assureur="insurerInformation.numeroContratAssureur"
      v-model:nom-contrat-assureur="insurerInformation.nomContratAssureur"
      :server-errors="serverErrors"
    />

    <FriendlyCaptcha 
      sitekey="FCMVPCK1VET1ETII" 
      @solution-found="onCaptchaSolution"
    />

    <sl-button
      data-cy="action-submit"
      @click="submit()"
    >
      Souscrire
    </sl-button>

    <sl-separator class="mt-4 mb-4" />
  </div>
</template>

<script>

import { useVuelidate } from '@vuelidate/core'
import BankInformationForm from "src/modules/souscription/components/BankInformationForm";
import ContributionInformationForm from "src/modules/souscription/components/ContributionInformationForm";
import InsurerInformationForm from "src/modules/souscription/components/InsurerInformationForm";
import PersonalInformationForm from "src/modules/souscription/components/PersonalInformationForm";
import SlButton from "src/modules/shared/components/SlButton";
import SlSeparator from "src/modules/shared/components/SlSeparator";
import SouscriptionVigeoService from "src/modules/souscription/services/souscription-vigeo.service";
import {reactive, ref, onMounted, computed} from "vue";
import {numSecuToString, stringToNumSecu} from "src/modules/shared/utils/numSecuUtils";
import {organismeToString, stringToOrganisme} from "src/modules/shared/utils/organismeUtils";
import {ibanToString, stringToIban} from "src/modules/shared/utils/ibanUtils";
import useDialog from "src/modules/shared/composable/useDialog";
import FriendlyCaptcha from "../../shared/components/FriendlyCaptcha";
import SlHeader from "src/modules/shared/components/SlHeader";

export default {
  name: "SouscriptionVigeo",
  components: {
    BankInformationForm,
    ContributionInformationForm,
    InsurerInformationForm,
    PersonalInformationForm,
    SlButton,
    SlHeader,
    SlSeparator,
    FriendlyCaptcha,
  },
  emits: [
    'goto'
  ],
  setup() {
    const content = ref('')
    const initialState = window.__INITIAL_STATE__ || {}
    const {showLoaderLoading, showLoaderSending, showPopinError, hideLoader} = useDialog()
    const customUserData = reactive({})

    const personalInformation = reactive({
      numSecu: '',
      organisme: '',
      numSecuConjoint: '',
      organismeConjoint: '',
    })

    const contributionInformation = reactive({
      fractionnement: '',
      jourPrelevement: '',
      dateEffet: '',
    })

    const bankInformation = reactive({
      nomTitulaire: '',
      iban: '',
      dateEffet: '',
      nomBanque: '',
      adresseBanque: '',
      codePostalBanque: '',
      villeBanque: '',
    })

    const insurerInformation = reactive({
      nomAssureur: '',
      adresseAssureur: '',
      codePostalAssureur: '',
      villeAssureur: '',
      paysAssureur: '',
      numeroContratAssureur: '',
      nomContratAssureur: '',
    })

    const serverErrors = ref([])

    const fractionnements = ref([])
    const joursPrelevement = ref([])

    const displayConjoint = computed(() => {
      if (customUserData.value && customUserData.value.conjoint === '1' && customUserData.value.ayantDroit === '2') {
        return true
      }
      return false
    })

    const displayAssureur = computed(() => {
      if (customUserData.value && customUserData.value.couvertureSante === '1') {
        return true
      }
      return false
    })

    const headerLinks = computed(() => [
      customUserData.value ? customUserData.value.devoirDeConseilUrl : null,
      customUserData.value ? customUserData.value.quoteUrl : null,
    ])

    const captchaSolution = ref('')

    onMounted(async () => {

      await SouscriptionVigeoService.getUserInformations().then(res => {
        console.debug('[SouscriptionVigeo]', 'getUserInformations', res.data)
        customUserData.value = res.data

        personalInformation.numSecu = numSecuToString(customUserData.value.numSecu) ?? ''
        personalInformation.organisme = organismeToString(customUserData.value.organisme) ?? ''
        personalInformation.numSecuConjoint = numSecuToString(customUserData.value.numSecuConjoint) ?? ''
        personalInformation.organismeConjoint = organismeToString(customUserData.value.organismeConjoint) ?? ''

        contributionInformation.dateEffet = customUserData.value.dateEffet ?? ''
        contributionInformation.fractionnement = customUserData.value.fractionnement ?? ''
        contributionInformation.jourPrelevement = customUserData.value.jourPrelevement ?? ''

        bankInformation.nomTitulaire = customUserData.value.nomTitulaire ?? ''
        bankInformation.iban = ibanToString(customUserData.value.iban) ?? ''
        bankInformation.dateEffet = customUserData.value.dateEffet ?? ''
        bankInformation.nomBanque = customUserData.value.nomBanque ?? ''
        bankInformation.adresseBanque = customUserData.value.adresseBanque ?? ''
        bankInformation.codePostalBanque = customUserData.value.codePostalBanque ?? ''
        bankInformation.villeBanque = customUserData.value.villeBanque ?? ''

        if (displayAssureur.value) {
          insurerInformation.nomAssureur = customUserData.value.nomAssureur ?? ''
          insurerInformation.adresseAssureur = customUserData.value.adresseAssureur ?? ''
          insurerInformation.codePostalAssureur = customUserData.value.codePostalAssureur ?? ''
          insurerInformation.villeAssureur = customUserData.value.villeAssureur ?? ''
          insurerInformation.paysAssureur = customUserData.value.paysAssureur ?? ''
          insurerInformation.numeroContratAssureur = customUserData.value.numeroContratAssureur ?? ''
          insurerInformation.nomContratAssureur = customUserData.value.nomContratAssureur ?? ''
        }
      })

      // Afin de pouvoir fixer l'ordre, on met en dur ces données qui doivent correspondre à celles du back
      fractionnements.value = [
        {label: 'Sélectionner', value: ''},
        {label: 'Mensuel', value: '4'},
        {label: 'Trimestriel', value: '3'},
        {label: 'Semestriel', value: '2'},
        {label: 'Annuel', value: '1'},
      ]

      joursPrelevement.value = [
        {label: 'Sélectionner', value: ''},
        {label: '5', value: '1'},
        {label: '10', value: '2'},
        {label: '20', value: '3'},
        {label: 'Dernier jour du mois', value: '4'},
      ]
    })

    return {
      v$: useVuelidate(),
      customUserData,
      initialState,
      content,
      personalInformation,
      contributionInformation,
      bankInformation,
      insurerInformation,
      fractionnements,
      joursPrelevement,
      displayConjoint,
      displayAssureur,
      serverErrors,
      showLoaderLoading,
      showLoaderSending,
      showPopinError,
      hideLoader,
      headerLinks,
      captchaSolution
    }
  },
  methods: {
    onCaptchaSolution(solution) {
      this.captchaSolution = solution
    },
    async submit() {
      console.debug("[SouscriptionVigeo]", "submit")

      // On reset les erreurs serveurs
      this.serverErrors = []

      // On valide les formulaires
      this.v$.$touch()
      console.error(this.v$.$errors)

      // Hack : On regarde si on a uniquement des erreurs serveurs car elles ne partent pas d'elles-mêmes...
      let onlyServerError = true
      for (const error of this.v$.$errors) {
        if (error.$validator !== 'serverError') onlyServerError = false
      }

      // En cas d'erreur, on ne fait rien (les composants vont afficher d'eux-mêmes les erreurs)
      if (this.v$.$error && !onlyServerError) return

      // En cas de succès, on récupère les différentes données des composants pour les envoyer
      const data = {
        ...this.personalInformation,
        ...this.contributionInformation,
        ...this.bankInformation,
        ...this.insurerInformation,
        captcha: this.captchaSolution
      }

      // On réarrange les données pour qu'elles soient conformes à ce qu'attend notre API
      const cleanData = this.cleanData(data)

      const param = {
        // zone: this.initialState.zoneId,
        // page: this.initialState.pageId,
        // iframe_referer: this.initialState.iframe_referer
      }

      await this.showLoaderSending()
      console.debug("[SouscriptionVigeo]", "submit", "param", param)

      SouscriptionVigeoService.submitForm(cleanData, param).then(res => {
        console.debug("[SouscriptionVigeo]", "submitForm", "res", res)
        this.$emit('goto', 'recapitulatif-vigeo')
      }).catch(error => {
        console.error("[SouscriptionVigeo]", "submit", "error", error.response.data.msg)

        // Si on détecte des erreurs serveurs, on met la variable à jour
        if (error.response.data.violations !== undefined) {
          this.serverErrors = error.response.data.violations
        } else {
          const errorMsg = error.response.data.msg ?? 'Erreur serveur'
          this.showPopinError(errorMsg)
        }

        console.error(this.serverErrors)
      }).finally(() => {
        this.hideLoader()
      })
    },
    /**
     * Formate les données du formulaire dans le format attendu
     *
     * @param data
     * @returns {any}
     */
    cleanData(data) {
      const cleanData = JSON.parse(JSON.stringify(data))

      cleanData.numSecu = stringToNumSecu(data.numSecu)
      cleanData.organisme = stringToOrganisme(data.organisme)
      cleanData.numSecuConjoint = stringToNumSecu(data.numSecuConjoint)
      cleanData.organismeConjoint = stringToOrganisme(data.organismeConjoint)
      cleanData.iban = stringToIban(data.iban)

      // On supprime les champs non utilisés
      delete cleanData.dateEffet

      if (!this.displayConjoint) {
        delete cleanData.numSecuConjoint
        delete cleanData.organismeConjoint
      }

      if (!this.displayAssureur) {
        delete cleanData.nomAssureur
        delete cleanData.adresseAssureur
        delete cleanData.codePostalAssureur
        delete cleanData.villeAssureur
        delete cleanData.paysAssureur
        delete cleanData.numeroContratAssureur
        delete cleanData.nomContratAssureur
      }

      return cleanData
    }
  }
}
</script>

<style scoped>

</style>
